import { Box } from '@mui/material';
import PlayIcon from '../../assets/images/PlayIcon.png';
import Checkmark from '../../assets/images/Checkmark.png';
import { useDispatch } from 'react-redux';
import * as appActions from '../../Actions/app';
import './PlayContainer.css';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useCallback, useEffect } from 'react';
import { Replay } from '@mui/icons-material';
import { LevelData } from 'Types';

interface SelectedLevelProgress {
  highest_tier_complete?: number;
}

interface PlayContainerProps {
  selectedLevel: LevelData;
  levelSelect: number;
  level_number: number;
  name: string;
  description: string;
  selectedLevelProgress: SelectedLevelProgress | null | undefined;
  tierCountForLevel: number;
}

export const PlayContainer = (props: PlayContainerProps) => {
  const {
    selectedLevel, //object
    levelSelect,
    level_number,
    name,
    description,
    selectedLevelProgress,
    tierCountForLevel,
  } = props;
  let highestTierComplete = 0;
  if (selectedLevelProgress) {
    highestTierComplete = selectedLevelProgress.highest_tier_complete;
  }

  return (
    <Box className={`container-styles-1`}>
      <Box className="container-styles-2">
        <Box sx={{ height: '100%', padding: '30px', width: level_number == 0 ? '100%' : '50%' }}>
          <Box className="container-styles-3">
            <Box className="play-container-text-container">
              <p className="play-container-text-description">LEVEL {level_number}:&nbsp; </p>
              <p className="play-container-text-description"> {name}</p>
            </Box>
            <p className="play-container-text">{description}</p>
          </Box>
          <Completion
            highestTierComplete={highestTierComplete}
            tierCountForLevel={tierCountForLevel}
          />
        </Box>
        {level_number != 0 && (
          <Box
            sx={{
              width: '50%',
              minHeight: '200px',
              marginRight: '20px',
              textAlign: 'left',
              // backgroundColor: '#B9BEC4' // the new color once we update the png
              backgroundColor: 'rgba(226,226,226)',
              margin: '0px',
            }}
          >
            <Box sx={{ marginLeft: '28px', marginTop: '20px' }} className={'preview-text'}>
              PREVIEW
            </Box>
            <Box
              sx={{
                margin: 'auto',
                padding: '3px 5px 1px 5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <img
                style={{ maxWidth: '100%', maxHeight: '150px' }}
                src={`https://${selectedLevel.srcs}`}
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            minWidth: '160px',
            maxWidth: '160px',
            margin: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Play
            levelSelect={levelSelect}
            level_number={level_number}
            highestTierComplete={highestTierComplete}
          />
          <ReplayTutorialButton
            level_number={level_number}
            highestTierComplete={highestTierComplete}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface ReplayTutorialButtonProps {
  level_number: number;
  highestTierComplete: number;
}

const ReplayTutorialButton = ({ level_number, highestTierComplete }: ReplayTutorialButtonProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onReplayTutorial = useCallback(() => {
    dispatch(appActions.fetchTutorialData());
    navigate(`/tutorial`);
  }, [dispatch, navigate]);
  // Don't show button if level is 0 or no tiers are complete
  if (level_number == 0 || highestTierComplete == 0) {
    return null;
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={onReplayTutorial}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: '5px',
        marginTop: '10px',
        minHeight: '44px',
        border: 0,
        background: 'transparent',
        appearance: 'none',
        WebkitAppearance: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        cursor: 'pointer',
        color: '#CA7C62',
        filter: 'brightness(0.75)',
      }}
    >
      <Replay sx={{ width: '18px', height: '18px' }} />
      <span style={{ fontSize: '14px', fontWeight: '700', textDecoration: 'underline' }}>
        Replay Tutorial
      </span>
    </Box>
  );
};

interface PlayProps {
  levelSelect: number;
  level_number: number;
  highestTierComplete: number;
}

const Play = ({ levelSelect: _levelSelect, level_number, highestTierComplete }: PlayProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onPlay = useCallback(() => {
    // Will grab tutorials and redirect in the sagas
    dispatch(appActions.fetchTutorialData());
    // Level 0 is tutorial-only (no sight reading stage), so always go to tutorial
    // For other levels, skip tutorial if user has completed at least one tier
    if (highestTierComplete >= 1 && level_number > 0) {
      navigate(`/lesson`);
    } else {
      navigate(`/tutorial`);
    }
  }, [dispatch, navigate, highestTierComplete, level_number]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        onPlay();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onPlay]);
  return (
    <Box>
      <Box className={'play-text-styles'}>PLAY</Box>
      <Box
        component="button"
        type="button"
        aria-label="Play selected roadmap level"
        onClick={() => {
          onPlay();
        }}
        className="play-container-styles"
      >
        <img src={PlayIcon} />
      </Box>
    </Box>
  );
};

export const Completion = (props: { highestTierComplete: number; tierCountForLevel: number }) => {
  const { highestTierComplete, tierCountForLevel } = props;
  const completeColor = '#6F87F1';
  const incompleteColor = '#767C8C';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        height: '40px',
        marginTop: '6px',
      }}
    >
      {highestTierComplete == 0 && ( // Only place checkmark at start if no progress
        <Box sx={{ marginRight: '10px' }}>
          <Box
            sx={{
              ...circleStyles,
              backgroundColor: incompleteColor,
              marginLeft: '12px',
              marginRight: '12px',
            }}
          />
        </Box>
      )}
      {[...Array(tierCountForLevel)].map((k, v) => {
        // logger.info("T and v?", highestTierComplete, v)
        return (
          <Box
            key={uuid()}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {/* Place circle at start instead of checkmkark if more than one is complete */}
            {highestTierComplete >= 1 && v == 0 && (
              <Box
                sx={{
                  ...circleStyles,
                  backgroundColor: highestTierComplete >= v + 1 ? completeColor : incompleteColor,
                  marginLeft: '12px',
                  marginRight: '12px',
                }}
              />
            )}
            <Box
              sx={{
                ...lineStyles,
                backgroundColor: highestTierComplete >= v + 1 ? completeColor : incompleteColor,
              }}
            />
            {/* Place circle or check depending if current tier is the highest complete */}
            {highestTierComplete == v + 1 ? (
              <Box sx={{ marginLeft: '10px', marginRight: '10px' }}>
                <img src={Checkmark} />
              </Box>
            ) : (
              <Box
                sx={{
                  ...circleStyles,
                  backgroundColor: highestTierComplete >= v + 1 ? completeColor : incompleteColor,
                  marginLeft: '12px',
                  marginRight: '12px',
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const lineStyles = {
  height: '5px',
  borderRadius: '99px',
  flexGrow: 1, // Line will take up full width between cirlces (100% width was squishing)
};

const circleStyles = {
  width: '14px',
  height: '14px',
  borderRadius: '50%',
};
